import axios from "axios";
import { create } from "zustand";
import { farmageo_api } from "../../../config";

const appendMessage = (chat = [], message) => [...chat, message];
const normalizeAttachment = (attachment) => {
    if (!attachment) return null;
    if (typeof attachment === "string") {
        return { url: attachment, name: "Imagen adjunta" };
    }
    return attachment;
};

export const useAI = create((set, get) => ({
    chats: {},
    interactionIdByChat: {},
    idSIAConversacionByChat: {},
    loadingByChat: {},
    errorByChat: {},
    isChatOpen: false,
    currentChat: null,
    datos: {},
    currentAiConf: {},

    openChat: (id_a, datos, aiconf) =>
        set((state) => ({
            isChatOpen: id_a ? true : !state.isChatOpen,
            currentChat: id_a ?? state.currentChat,
            datos: { ...state.datos, [id_a]: datos },
            currentAiConf: aiconf
        })),

    closeChat: () =>
        set(() => ({
            isChatOpen: false,
        })),

    toggleChat: (id_a) =>
        set((state) => ({
            isChatOpen:
                state.currentChat === id_a ? !state.isChatOpen : true,
            currentChat: id_a ?? state.currentChat,
        })),

    setCurrentChat: (id_a) =>
        set(() => ({
            currentChat: id_a,
        })),

    addMessage: (id_a, message) =>
        set((state) => ({
            chats: {
                ...state.chats,
                [id_a]: appendMessage(state.chats[id_a], message),
            },
        })),

    clearChat: (id_a) =>
        set((state) => ({
            chats: {
                ...state.chats,
                [id_a]: [],
            },
            interactionIdByChat: {
                ...state.interactionIdByChat,
                [id_a]: null,
            },
            idSIAConversacionByChat: {
                ...state.idSIAConversacionByChat,
                [id_a]: null,
            },
            errorByChat: {
                ...state.errorByChat,
                [id_a]: null,
            },
        })),

    clearError: (id_a) =>
        set((state) => ({
            errorByChat: {
                ...state.errorByChat,
                [id_a]: null,
            },
        })),

    sendMessage: async (id_a, text = "", attachment = null) => {
        const content = String(text ?? "").trim();
        if (!id_a || (!content && !attachment)) return null;

        const state = get();
        const isFirstMessage = (state.chats?.[id_a]?.length ?? 0) === 0;
        const data = state.datos?.[id_a] ?? {};
        const interactionId = state.interactionIdByChat?.[id_a];
        const idSIAConversacion = state.idSIAConversacionByChat?.[id_a];
        const formData = new FormData();

        formData.append("id_a", id_a);
        formData.append("text", content);
        formData.append("data", JSON.stringify(data));
        if (isFirstMessage) {
            formData.append("first", "true");
        }
        if (interactionId) {
            formData.append("interaction_id", interactionId);
        }
        if (!isFirstMessage && idSIAConversacion) {
            formData.append("id_s_ia_conversacion", idSIAConversacion);
        }

        if (attachment?.file) {
            formData.append("archivo", attachment.file);
        }

        const userMessage = {
            role: "user",
            content,
            first: isFirstMessage,
            attachment: normalizeAttachment(attachment),
            createdAt: new Date().toISOString(),
        };

        set((state) => ({
            chats: {
                ...state.chats,
                [id_a]: appendMessage(state.chats[id_a], userMessage),
            },
            loadingByChat: {
                ...state.loadingByChat,
                [id_a]: true,
            },
            errorByChat: {
                ...state.errorByChat,
                [id_a]: null,
            },
            currentChat: id_a,
            isChatOpen: true,
        }));

        try {
            const aiResponse = await axios.post(
                `${farmageo_api}/aiRequest`,
                formData,
            );
            if (aiResponse.status >= 400) throw aiResponse

            const aiMessage = {
                role: "ai",
                content: aiResponse?.data?.content ?? aiResponse?.data?.airesponse ?? "",
                interaction_id: aiResponse?.data?.interaction_id,
                id_s_ia_conversacion: aiResponse?.data?.id_s_ia_conversacion,
                attachment: normalizeAttachment(aiResponse?.data?.image),
                createdAt: new Date().toISOString(),
                raw: aiResponse?.data,
            };

            set((state) => ({
                chats: {
                    ...state.chats,
                    [id_a]: appendMessage(state.chats[id_a], aiMessage),
                },
                interactionIdByChat: {
                    ...state.interactionIdByChat,
                    [id_a]: aiResponse?.data?.interaction_id ?? state.interactionIdByChat?.[id_a] ?? null,
                },
                idSIAConversacionByChat: {
                    ...state.idSIAConversacionByChat,
                    [id_a]: aiResponse?.data?.id_s_ia_conversacion ?? state.idSIAConversacionByChat?.[id_a] ?? null,
                },
                loadingByChat: {
                    ...state.loadingByChat,
                    [id_a]: false,
                },
            }));

            return aiResponse.data;
        } catch (error) {
            console.log(error)
            const errorMessage =
                error?.response?.data?.error?.message ??
                error?.response?.data?.message ??
                error?.data?.error.message ??
                error?.message ??
                "No se pudo obtener respuesta de la IA";

            const aiErrorMessage = {
                role: "ai",
                content: errorMessage,
                isError: true,
                createdAt: new Date().toISOString(),
                raw: error?.response?.data ?? error,
            };

            set((state) => ({
                chats: {
                    ...state.chats,
                    [id_a]: appendMessage(state.chats[id_a], aiErrorMessage),
                },
                loadingByChat: {
                    ...state.loadingByChat,
                    [id_a]: false,
                },
                errorByChat: {
                    ...state.errorByChat,
                    [id_a]: errorMessage,
                },
            }));

            throw error;
        }
    },
}));

export default useAI;
